import type {
  ChatAiReplyJob,
  ChatMessage,
  ChatSession,
  ChatStreamHandle,
  PostChatMessageResult,
} from '@/types/widget'
import { getDeviceId } from '@/utils/device'
import { asTrimmedId } from '@/utils/ids'
import type { SafeFetchResponse } from '../index'
import { API_BASE } from './index'

const SHOPPER_SESSION_HEADER = 'X-Astrobot-Shopper-Session'
const ASTROBOT_VERSION_HEADER = 'X-Astrobot-Version'
const ASTROBOT_VERSION_V2 = '2'

/** Matches astrobot-shopper `maxPromptSubmissionChars` (Go rune count). */
export const MAX_CHAT_MESSAGE_CHARS = 3000

export function chatMessageCharCount(text: string): number {
  return [...text].length
}

export function isChatMessageTooLong(text: string): boolean {
  return chatMessageCharCount(text) > MAX_CHAT_MESSAGE_CHARS
}

function chatBaseUrl(uid: string): string {
  return `${API_BASE}/astrobot-shopper/merchant/${encodeURIComponent(uid)}`
}

function chatHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    [SHOPPER_SESSION_HEADER]: getDeviceId(),
  }
  headers[ASTROBOT_VERSION_HEADER] = ASTROBOT_VERSION_V2
  return headers
}

/**
 * Unwrap the AIQ response wrapper. Mirrors the responseList helper in
 * astrobot-shopper-chat-client/app.js (lines 177–188): supports {data: [...]}
 * one or two levels deep and bare arrays.
 */
function unwrapList<T>(body: unknown): T[] {
  const v = body as { data?: unknown } | unknown[] | undefined
  if (Array.isArray(v)) return v as T[]
  if (v && typeof v === 'object') {
    const data = (v as { data?: unknown }).data
    if (Array.isArray(data)) return data as T[]
    if (data && typeof data === 'object') return unwrapList<T>(data)
  }
  return []
}

function unwrapRecord(body: unknown): Record<string, unknown> | undefined {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return undefined
  const v = body as Record<string, unknown>
  if (v.data && typeof v.data === 'object' && !Array.isArray(v.data) && !v.stream && !v.message && !v.job) {
    return unwrapRecord(v.data)
  }
  return v
}

function readTimestamp(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value
  return undefined
}

/** Map shopper API messages (`created`) onto the widget `createdAt` field. */
export function normalizeChatMessage(raw: unknown): ChatMessage | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const m = raw as Record<string, unknown>
  if (typeof m.role !== 'string' && typeof m.content !== 'string') return undefined
  const createdAt = readTimestamp(m.createdAt) ?? readTimestamp(m.created)
  return {
    ...(typeof m.id === 'string' ? { id: m.id } : {}),
    role: typeof m.role === 'string' && m.role.trim() ? m.role : 'shopper',
    content: typeof m.content === 'string' ? m.content : '',
    ...(createdAt ? { createdAt } : {}),
  }
}

function unwrapStream(body: unknown): ChatStreamHandle | undefined {
  const v = unwrapRecord(body)
  if (!v) return undefined
  if (v.stream && typeof v.stream === 'object') {
    const s = v.stream as { path?: unknown; wsAuthTicket?: unknown; jobID?: unknown }
    if (typeof s.path === 'string' && typeof s.wsAuthTicket === 'string' && s.wsAuthTicket.trim() !== '') {
      return {
        path: s.path,
        wsAuthTicket: s.wsAuthTicket,
        ...(typeof s.jobID === 'string' ? { jobID: s.jobID } : {}),
      }
    }
  }
  if (v.data && typeof v.data === 'object') return unwrapStream(v.data)
  return undefined
}

function unwrapJob(raw: unknown): ChatAiReplyJob | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const j = raw as Record<string, unknown>
  if (typeof j.id !== 'string' || !j.id.trim()) return undefined
  return {
    id: j.id,
    ...(typeof j.sessionID === 'string' ? { sessionID: j.sessionID } : {}),
    ...(typeof j.status === 'string' ? { status: j.status } : {}),
    ...(typeof j.error === 'string' ? { error: j.error } : {}),
  }
}

function unwrapPostMessageResult(body: unknown): PostChatMessageResult {
  const root = unwrapRecord(body) ?? {}
  const stream = unwrapStream(root)
  const job = unwrapJob(root.job)
  const message = normalizeChatMessage(root.message)
  const session = root.session ? unwrapSession(root.session) : null
  if (stream && job?.id && !stream.jobID) {
    stream.jobID = job.id
  }
  return {
    ...(stream ? { stream } : {}),
    ...(message ? { message } : {}),
    ...(session ? { session } : {}),
    ...(job ? { job } : {}),
  }
}

/**
 * Unwrap a session-shaped payload. Mirrors responseSession in
 * astrobot-shopper-chat-client/app.js (lines 190–202): supports {id, ...},
 * {data: {id, ...}}, and falls back to the first item of a list.
 */
function unwrapSession(body: unknown): ChatSession | null {
  const v = body as { id?: string; data?: unknown } | undefined
  if (v && typeof v === 'object') {
    if ((v as { id?: string }).id) return v as ChatSession
    const data = (v as { data?: unknown }).data
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      if ((data as { id?: string }).id) return data as ChatSession
      return unwrapSession(data)
    }
  }
  const first = unwrapList<ChatSession>(body)[0]
  return first ?? null
}

async function chatFetch(uid: string, path: string, options: RequestInit = {}): Promise<unknown> {
  const merchantUid = asTrimmedId(uid)
  if (!merchantUid) throw new Error('Merchant UID is required')

  const url = `${chatBaseUrl(merchantUid)}${path}`
  // Spread options first, then re-apply credentials/headers so callers cannot
  // accidentally drop X-Astrobot-Shopper-Session (or credentials: 'include').
  const { headers: optionHeaders, ...restOptions } = options
  const response = await fetch(url, {
    credentials: 'include',
    ...restOptions,
    headers: { ...chatHeaders(), ...(optionHeaders ?? {}) },
  })

  const contentType = response.headers.get('content-type') ?? ''
  const body = contentType.includes('application/json') ? await response.json() : await response.text()

  if (!response.ok) {
    const message =
      typeof body === 'string'
        ? body || response.statusText
        : (body?.error ?? body?.message ?? response.statusText ?? 'Request failed')
    throw new Error(message)
  }

  return body
}

export const DEFAULT_SHOPPER_SESSION_TITLE = 'Astrobot Shopper Chat'

export function isDefaultShopperSessionTitle(title?: string): boolean {
  const trimmed = (title ?? '').trim()
  if (!trimmed) return true
  return (
    trimmed === DEFAULT_SHOPPER_SESSION_TITLE ||
    trimmed === 'Astro Shopper Chat' ||
    trimmed.toLowerCase() === 'astrobot shopper chat'
  )
}

export function titleFromPrompt(prompt: string, max = 48): string {
  const cleaned = prompt.replace(/\s+/g, ' ').trim()
  if (!cleaned) return ''
  if (cleaned.length <= max) return cleaned
  return `${cleaned.slice(0, max - 1).trimEnd()}…`
}

export async function createChatSession(
  uid: string,
  options?: { storeID?: string; contactID?: string }
): Promise<SafeFetchResponse<ChatSession>> {
  try {
    // Leave title empty at create time — the first user prompt becomes the
    // session title when the message is posted (see postChatMessage). Include
    // any known shopper identity fields so the backend can associate the
    // session with the right store / contact from the very first request;
    // unknown fields are ignored by the astrobot-shopper handler.
    const payload: Record<string, unknown> = {}
    const storeID = asTrimmedId(options?.storeID)
    const contactID = asTrimmedId(options?.contactID)
    if (storeID) payload.storeID = storeID
    if (contactID) payload.contactID = contactID

    const body = await chatFetch(uid, '/sessions', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    const session = unwrapSession(body)
    if (!session?.id) {
      return [undefined, 'Session was created but no session ID was returned']
    }
    return [session, undefined]
  } catch (error) {
    return [undefined, error instanceof Error ? error.message : 'Failed to create chat session']
  }
}

export async function listChatSessions(
  uid: string,
  limit: number = 50
): Promise<SafeFetchResponse<ChatSession[]>> {
  try {
    const body = await chatFetch(uid, `/sessions?limit=${limit}`)
    return [unwrapList<ChatSession>(body), undefined]
  } catch (error) {
    return [undefined, error instanceof Error ? error.message : 'Failed to list chat sessions']
  }
}

export async function listChatMessages(
  uid: string,
  sessionId: string,
  limit: number = 100
): Promise<SafeFetchResponse<ChatMessage[]>> {
  const id = asTrimmedId(sessionId)
  if (!id) return [undefined, 'Session ID is required']
  try {
    const body = await chatFetch(uid, `/sessions/${encodeURIComponent(id)}/messages?limit=${limit}`)
    const messages = unwrapList<unknown>(body)
      .map((item) => normalizeChatMessage(item))
      .filter((item): item is ChatMessage => item != null)
    return [messages, undefined]
  } catch (error) {
    return [undefined, error instanceof Error ? error.message : 'Failed to load chat messages']
  }
}

export async function postChatMessage(
  uid: string,
  sessionId: string,
  message: string,
  storeID?: string,
  title?: string,
  contactID?: string
): Promise<SafeFetchResponse<PostChatMessageResult>> {
  const id = asTrimmedId(sessionId)
  if (!id) return [undefined, 'Session ID is required']
  const payload: Record<string, unknown> = { message }
  const trimmedStoreID = asTrimmedId(storeID)
  const trimmedContactID = asTrimmedId(contactID)
  if (trimmedStoreID) payload.storeID = trimmedStoreID
  // Client-supplied contactID is a hint for personalization; the backend still
  // gates PII access through the 2FA verification flow. Safe to include as an
  // additional field — unknown JSON fields are ignored by gserv.
  if (trimmedContactID) payload.contactID = trimmedContactID
  const trimmedTitle = titleFromPrompt(title ?? '')
  if (trimmedTitle) payload.title = trimmedTitle

  if (isChatMessageTooLong(message)) {
    return [undefined, `Message must be ${MAX_CHAT_MESSAGE_CHARS} characters or fewer`]
  }

  try {
    const body = await chatFetch(uid, `/sessions/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return [unwrapPostMessageResult(body), undefined]
  } catch (error) {
    return [undefined, error instanceof Error ? error.message : 'Failed to send chat message']
  }
}

/**
 * Requests contextual loading messages for a prompt. Mirrors the merchant
 * astro pattern in ecom-monorepo/apps/dash/src/api/astro/chat/astro.chat.queries.ts.
 * Backend is expected to expose POST /loading-messages returning
 * { messages: string[] }. If the endpoint is not yet available on the shopper
 * backend this returns an error and the caller falls back to static defaults.
 */
export async function fetchChatLoadingMessages(
  uid: string,
  prompt: string
): Promise<SafeFetchResponse<string[]>> {
  const trimmed = prompt.trim()
  if (!trimmed) return [undefined, 'Prompt is required']

  try {
    const body = await chatFetch(uid, '/loading-messages', {
      method: 'POST',
      body: JSON.stringify({ prompt: trimmed }),
    })
    const messages = (body as { messages?: unknown })?.messages
    if (Array.isArray(messages) && messages.every((m) => typeof m === 'string')) {
      return [messages as string[], undefined]
    }
    return [undefined, 'Invalid loading messages response']
  } catch (error) {
    return [undefined, error instanceof Error ? error.message : 'Failed to fetch loading messages']
  }
}
